import React, { useState, useEffect } from 'react';

interface DailyReport {
  projectName: string;
  date: string;
  txCount: number;
  uniqueBuyers: number;
  totalConfirmedUSD: number;
  highestPaymentUSD: number;
  averagePaymentUSD: number;
  ethPercentage: number | null;
  bnbPercentage: number | null;
  maticPercentage: number | null;
  usdtBep20Percentage: number | null;
  usdtErc20Percentage: number | null;
}

interface TotalReport {
  total_tx: number;
  total_revenue: number;
}

interface ApiResponse {
  status_code: number;
  message: string;
  response: {
    result: {
      daily_reports: DailyReport[];
      total_report: TotalReport;
    }
  }
}

const SalesPage: React.FC = () => {
  const [salesData, setSalesData] = useState<DailyReport[]>([]);
  const [totalReport, setTotalReport] = useState<TotalReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSalesData = async () => {
      setIsLoading(true);
      try {
        const response = await fetch('https://prs.teddypufftoken.com/daily/numbers/get');
        
        if (!response.ok) {
          throw new Error(`API yanıtı başarısız: ${response.status}`);
        }
        
        const data: ApiResponse = await response.json();
        
        if (data.status_code === 200 && data.response?.result?.daily_reports) {
          setSalesData(data.response.result.daily_reports);
          setTotalReport(data.response.result.total_report);
          setError(null);
        } else {
          throw new Error('API yanıtı geçerli veri içermiyor');
        }
      } catch (err) {
        console.error('Veri çekme hatası:', err);
        setError('Veriler yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSalesData();
  }, []);

  // Tarih formatını daha okunabilir hale getirme
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return new Date(dateString).toLocaleDateString('tr-TR', options);
  };

  // Para değerlerini formatlama
  const formatCurrency = (value: number) => {
    return value.toLocaleString('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Yüzde değerlerini formatlama
  const formatPercentage = (value: number | null) => {
    if (value === null || value === 0) return '-';
    return `%${value.toFixed(2)}`;
  };

  const renderPaymentMethod = (item: DailyReport) => {
    const methods = [];
    
    if (item.ethPercentage) methods.push(`ETH: ${formatPercentage(item.ethPercentage)}`);
    if (item.bnbPercentage) methods.push(`BNB: ${formatPercentage(item.bnbPercentage)}`);
    if (item.maticPercentage) methods.push(`MATIC: ${formatPercentage(item.maticPercentage)}`);
    if (item.usdtBep20Percentage) methods.push(`USDT-BEP20: ${formatPercentage(item.usdtBep20Percentage)}`);
    if (item.usdtErc20Percentage) methods.push(`USDT-ERC20: ${formatPercentage(item.usdtErc20Percentage)}`);
    
    return methods.length > 0 ? methods.join(', ') : '-';
  };

  return (
    <div className="min-h-screen bg-gray-100 pt-20">
      <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
        {totalReport && (
          <div className="bg-white rounded-lg shadow p-6 mb-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Toplam Özet</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-700 font-medium">Toplam İşlem Sayısı</p>
                <p className="text-2xl font-bold text-blue-800">{totalReport.total_tx}</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-green-700 font-medium">Toplam Gelir (USD)</p>
                <p className="text-2xl font-bold text-green-800">${formatCurrency(totalReport.total_revenue)}</p>
              </div>
            </div>
          </div>
        )}
        
        <div className="bg-white rounded-lg shadow p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Günlük Satış Raporu</h1>
          
          {isLoading ? (
            <div className="flex justify-center items-center py-10">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          ) : salesData.length === 0 ? (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded">
              Görüntülenecek veri bulunamadı.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tarih
                    </th>
                    <th className="py-3 px-6 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      İşlem Sayısı
                    </th>
                    <th className="py-3 px-6 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Benzersiz Alıcılar
                    </th>
                    <th className="py-3 px-6 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Toplam Satış (USD)
                    </th>
                    <th className="py-3 px-6 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      En Yüksek Ödeme (USD)
                    </th>
                    <th className="py-3 px-6 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ortalama Ödeme (USD)
                    </th>
                    <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ödeme Yöntemleri
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {salesData.map((item, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="py-4 px-6 text-sm font-medium text-gray-900">
                        {formatDate(item.date)}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-700 text-right">
                        {item.txCount}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-700 text-right">
                        {item.uniqueBuyers}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-700 text-right">
                        ${formatCurrency(item.totalConfirmedUSD)}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-700 text-right">
                        ${formatCurrency(item.highestPaymentUSD)}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-700 text-right">
                        ${formatCurrency(item.averagePaymentUSD)}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-700">
                        {renderPaymentMethod(item)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SalesPage; 